import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import Cookies from "js-cookie";

import { API_DOMAIN_MONITOR } from "../config/config";
import DefaultPageSize from "./Table/DefaultPageSize";

// Map snake_case dashboard params to DM camelCase expected by domain-monitor API
const PARAM_KEY_MAP = {
    monitoring_status: "monitoringStatus",
    business_type: "businessType",
    founded: "foundedYear",
    created_by: "createdBy",
    date_from: "dateFrom",
    date_to: "dateTo",
    sort_field: "sortBy",
    order: "sortOrder",
    size: "limit",
};

// Map sortBy values that have different casing on DM side
const SORT_FIELD_MAP = {
    foundedYear: "founded_year",
    businessType: "business_type",
    lastCheckedAt: "last_checked_at",
    createdAt: "created_at",
    monitoringStatus: "monitoring_status",
};

const translateParams = (input) => {
    const out = {};
    Object.entries(input).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") return;
        const key = PARAM_KEY_MAP[k] || k;
        let val = v;
        if (key === "sortBy" && SORT_FIELD_MAP[val]) val = SORT_FIELD_MAP[val];
        if (key === "sortOrder") val = String(val).toLowerCase();
        out[key] = val;
    });
    return out;
};

const buildQueryString = (params) => {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "") return;
        if (Array.isArray(v)) {
            if (v.length > 0) qp.append(k, v.join(","));
        } else {
            qp.append(k, v);
        }
    });
    return qp.toString();
};

const DataFetchHookDomainMonitor = (
    initialPath,
    requestMethod = "GET",
    defaultRequestData = { page: 1, size: 20, order: "DESC" },
    firstTimeAutoLoad = true
) => {
    const [data, setData] = useState({ datalist: [] });
    const [totalResult, setTotalResult] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [isError, setIsError] = useState(false);
    const [path, setPath] = useState(initialPath);
    const [requestData, setRequestData] = useState({ ...defaultRequestData, size: DefaultPageSize() });
    const [method, setMethod] = useState(requestMethod);
    const [isFirstTimeAutoLoad, setIsFirstTimeAutoLoad] = useState(firstTimeAutoLoad);

    const DATE_FORMAT = "YYYY-MM-DD";

    const getPage = () => (requestData && requestData.page ? requestData.page : 1);
    const getSize = () => (requestData && requestData.size ? requestData.size : 20);

    useEffect(() => {
        const fetchdata = async () => {
            if (!isFirstTimeAutoLoad) {
                setIsFirstTimeAutoLoad(true);
                return;
            }
            setIsLoading(true);
            setIsError(false);

            try {
                const token = Cookies.get("dm_token");
                if (!token) {
                    setIsError(true);
                    setErrorMsg("No domain monitor token");
                    setIsLoading(false);
                    return;
                }
                const options = { headers: { Authorization: "Bearer " + token } };

                // Dates only when the page sets them (Monitoring Alerts). Users/Domains have no date
                // filter, so no default range is sent.
                let param = { ...requestData, page: getPage(), size: getSize() };
                if (param.startDate) param.date_from = moment(param.startDate).format(DATE_FORMAT);
                if (param.endDate) param.date_to = moment(param.endDate).format(DATE_FORMAT);
                delete param.startDate;
                delete param.endDate;
                // Strip txshield-specific field that DM doesn't accept
                delete param.dm_userid;

                const translated = translateParams(param);
                const baseUrl = API_DOMAIN_MONITOR + "/v1" + path;

                let result;
                if (method === "POST") {
                    result = await axios.post(baseUrl, translated, options).catch(() => ({ data: false }));
                } else {
                    const qs = buildQueryString(translated);
                    const url = qs ? `${baseUrl}?${qs}` : baseUrl;
                    result = await axios.get(url, options).catch(() => ({ data: false }));
                }

                if (result && result.data) {
                    // DM: {success, data: [...], pagination: {total, ...}} OR {data, total}
                    const body = result.data;
                    const listData = Array.isArray(body.data) ? body.data : (Array.isArray(body.list) ? body.list : (Array.isArray(body.datalist) ? body.datalist : []));
                    setData({ datalist: listData });
                    const total = body.pagination && body.pagination.total !== undefined
                        ? body.pagination.total
                        : (body.totalResult !== undefined ? body.totalResult : (body.total !== undefined ? body.total : listData.length));
                    setTotalResult(parseInt(total) || 0);
                    if (body.msg) {
                        setErrorMsg(body);
                        setIsError(true);
                    }
                } else {
                    setIsError(true);
                    setErrorMsg("Failed to load data. Please try again.");
                }
            } catch (err) {
                setIsError(true);
                setErrorMsg(err.message);
            }
            setIsLoading(false);
        };
        fetchdata();
    }, [path, requestData, method]); // eslint-disable-line react-hooks/exhaustive-deps

    return [{ data, totalResult, requestData, isLoading, isError, errorMsg }, setPath, setRequestData, setMethod];
};

export default DataFetchHookDomainMonitor;
